import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import { ErrorBoundary } from './components/ErrorBoundary'
import { APP_BASE_PATH, getApiBaseUrl, getRuntimeErrorApiUrl, getTrustedDeviceApiBaseUrl } from './config'
import { recordBrowserOnError, recordBrowserUnhandledRejection } from '@secureblob/sdk/browserRuntimeErrors'
import { installRuntimeErrorTelemetryForwarding } from './runtimeErrorForwarding'
import { installBrowserDebugAgent, registerBrowserDebugHandle } from './browserDebug'
import './index.css'

if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
        const swPath = `${APP_BASE_PATH.replace(/\/+$/, '') || ''}/service-worker.js`
        try {
            const registration = await navigator.serviceWorker.register(swPath)

            // Detect when a new SW version is found and installed
            registration.addEventListener('updatefound', () => {
                const newWorker = registration.installing
                if (!newWorker) return
                newWorker.addEventListener('statechange', () => {
                    // Only reload on UPDATE, not on first-ever install
                    if (newWorker.state === 'activated' && navigator.serviceWorker.controller) {
                        console.info('[SW] New version activated, reloading…')
                        window.location.reload()
                    }
                })
            })

            // Proactively check for SW updates when user returns to the tab.
            // Browsers only auto-check on navigation or every 24h — this catches
            // new deploys within seconds for long-lived tabs / standalone PWAs.
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') {
                    registration.update().catch(() => { })
                }
            })
        } catch (error: unknown) {
            console.warn('Service worker registration failed', error)
        }
    })
}

// §389: Install global runtime error listeners so unhandled exceptions
// and rejected promises are captured into the shared buffer before any
// consumer (ErrorBoundary, runtimeDebugSnapshot, host ingress) reads them.
if (typeof window !== 'undefined') {
    window.addEventListener('error', (event: ErrorEvent) => {
        try {
            recordBrowserOnError(
                event.error?.message ?? event.message ?? '(unknown)',
                event.filename ?? window.location.href,
                event.lineno ?? 0,
                event.colno ?? 0,
                event.error,
            )
        } catch {
            // Best-effort only.
        }
    })

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        try {
            recordBrowserUnhandledRejection(event.reason)
        } catch {
            // Best-effort only.
        }
    })
}

// §390: Wire telemetry forwarder into page lifecycle.
// Uses the shared installRuntimeErrorTelemetryForwarding which:
//   - derives the correct /api/runtime-errors endpoint via getRuntimeErrorApiUrl()
//   - uses the §389 async forwarder with persistent forwarded-key deduplication
//   - has in-flight deduplication and test coverage
//   - returns a dispose function for cleanup
installRuntimeErrorTelemetryForwarding()
registerBrowserDebugHandle({
    name: 'secureblob.webapp.config',
    description: 'SecureBlob webapp runtime URL helpers and Vite mode flags.',
    handle: {
        appBasePath: APP_BASE_PATH,
        apiBaseUrl: () => getApiBaseUrl(),
        trustedDeviceApiBaseUrl: () => getTrustedDeviceApiBaseUrl(),
        runtimeErrorApiUrl: () => getRuntimeErrorApiUrl(),
        mode: import.meta.env.MODE,
        prod: import.meta.env.PROD,
    },
})
registerBrowserDebugHandle({
    name: 'secureblob.sdk.import',
    description: 'Lazy import handle for the SecureBlob SDK module used by this client bundle.',
    handle: {
        importSdk: () => import('@secureblob/sdk'),
    },
})
installBrowserDebugAgent()

ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <ErrorBoundary>
            <App />
        </ErrorBoundary>
    </React.StrictMode>,
)
